import {
  BalanceContainer,
  CoinName,
  CoinSymbol,
  Column,
  Image,
  Option,
  Row,
} from './styles';
import { AutocompleteSelectorOptionWithImageProps } from './props';
import { WalletService } from '../../../../services';
import { ToolTip } from '../../../Tooltip';
import { useMemo } from 'react';
import { useTranslation } from '@starsoft/common/hooks';
import { SettingsService } from '@starsoft/common/services/SettingsService';
import { getMoneyOrAmount } from '@starsoft/common/utils';

/**
 * Component that renders a single account option in the autocomplete selector
 * Displays account details including coin icon, name, balance and dollar value
 *
 * @param {Object} props - Component props
 * @param {Account} props.option - The account data to display
 * @param {boolean} props.selected - Whether this option is currently selected
 * @param {Function} props.handleSelect - Callback function when option is selected
 * @param {boolean} props.isInvestmentWallet - Whether this is an investment wallet account
 * @returns {JSX.Element} Account option component
 */
export function AutocompleteSelectorAccount({
  option,
  selected,
  handleSelect,
  isInvestmentWallet,
}: AutocompleteSelectorOptionWithImageProps) {
  const { t } = useTranslation('common');

  // Format the account balance with proper currency masking
  const formattedValue = WalletService.maskCurrency({
    amount: getMoneyOrAmount(option?.balance),
    coin: option?.coin,
  });

  /**
   * Calculates the dollar value of the account balance
   * Multiplies the balance by the coin price
   *
   * @returns {number} Balance in dollars
   */
  function getDollarBalance() {
    const balance = option?.balance?.copyWith({
      options: {
        scale: option?.coin?.decimals,
      },
    });

    return balance.multiply(getMoneyOrAmount(option?.coin?.price))?.toNumber();
  }

  // Memoize the dollar balance calculation
  const dollarBalance: number = useMemo(getDollarBalance, [option]);

  return (
    <Option selected={selected} onClick={() => handleSelect(option)}>
      <Row>
        <Image
          width={50}
          height={50}
          alt={`coin-icon-${option?.coin?.name}`}
          format="square"
          src={`${SettingsService.env.S3_BUCKET_BASE_URL}/coins/rounded/${option?.coin?.image}`}
          secondarySkeleton
        />
        <Column>
          <CoinSymbol>{option?.coin?.code}</CoinSymbol>
          <CoinName>{option?.coin?.name}</CoinName>
        </Column>
      </Row>

      <BalanceContainer>
        <span className="balance">{formattedValue}</span>
        <span className="value">
          {WalletService.maskCurrency({
            amount: dollarBalance,
          })}
        </span>

        {/* Show profit information for investment wallets */}
        {isInvestmentWallet && (
          <ToolTip titleComponent={t('profit_investment')}>
            <span className="value">
              {WalletService.maskCurrency({
                amount: getMoneyOrAmount(option?.profit),
                coin: option?.coin,
              })}
            </span>
          </ToolTip>
        )}
      </BalanceContainer>
    </Option>
  );
}
